import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import classNames from 'classnames';
import moment, { Moment } from 'moment';

import {
  AGE_OF_MAJORITY,
  displayDateFormatNoDay,
  MARKET_DATA_DATE,
  SINGLE_DATE_PICKER_FORMAT,
} from '../../lib/constants/date-time.constants';
import lang from '../../lib/language';
import {
  datesDifference,
  getDateTime,
  getMaxAllowedDateByAge,
  isBeforeDate,
  toMomentDate,
} from '../../lib/util/DateTimeHelpers';
import { NullableString } from '../../lib/util/types';

import CalendarIcon from '../../assets/img/date-range-icon.svg';
import { useBrowserWidth, useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { ZOOM_BEHAVIOR_WIDTH } from '../../util/constants';

import {
  createCalendarIconKeyDownHandler,
  createDateInputKeyDownHandler,
  getFormattedDateInputValue,
} from './helpers';
import { usePickerStyles } from './usePickerStyles';

import styles from './SingleDatePickerWrapper.module.scss';

type Props = {
  label?: string;
  value?: NullableString;
  minDate?: Date | string;
  maxDate?: Date | string;
  className?: string;
  idInput?: string;
  idPopover?: string;
  disabled?: boolean;
  onDateChange?: (date: NullableString) => void;
};

const toIso = (momentDate?: moment.Moment | null): NullableString =>
  (momentDate ? momentDate.toISOString() : null);

const SingleDatePickerWrapper = ({
  label,
  value,
  minDate,
  maxDate,
  className,
  idInput,
  idPopover,
  disabled = false,
  onDateChange,
}: Props): JSX.Element => {
  const pickerClasses = usePickerStyles();
  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();

  const maxAllowedDate = getMaxAllowedDateByAge(AGE_OF_MAJORITY);
  const maxDateMoment = toMomentDate(maxDate) ?? maxAllowedDate;
  const minDateMoment = toMomentDate(minDate);

  const [isCalendarVisible, setIsCalendarVisible] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const [temporarySelectedDate, setTemporarySelectedDate] = useState<moment.Moment | undefined>();
  const [selectedDate, setSelectedDate] = useState<moment.Moment | undefined>();

  const [inputValue, setInputValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    const initialDateMoment = toMomentDate(value ?? null);
    setSelectedDate(initialDateMoment);
    setTemporarySelectedDate(initialDateMoment);
    setInputValue(initialDateMoment ? initialDateMoment.format(SINGLE_DATE_PICKER_FORMAT) : '');
  }, [value]);

  const minDateValue = minDateMoment?.toDate();
  const maxDateValue = maxDateMoment?.toDate();

  const clamp = (dateToClamp: moment.Moment) => {
    let normalizedDate = dateToClamp.clone().startOf('day');
    if (minDateMoment && normalizedDate.isBefore(minDateMoment, 'day')) {
      normalizedDate = minDateMoment.clone();
    }
    if (maxDateMoment && normalizedDate.isAfter(maxDateMoment, 'day')) {
      normalizedDate = maxDateMoment.clone();
    }
    return normalizedDate;
  };

  const openCalendarFromIcon = () => {
    if (disabled) return;
    setIsCalendarVisible(true);
    setAnchorElement(isMobile ? null : (anchorRef.current ?? inputRef.current));
    setTemporarySelectedDate(selectedDate);
  };

  const closeCalendar = () => {
    setIsCalendarVisible(false);
    setAnchorElement(null);
    setTemporarySelectedDate((previousTempDate) =>
      (selectedDate && previousTempDate !== selectedDate ? selectedDate : previousTempDate));
  };

  const onShownMonthChange = (visibleMonthDate: Date | moment.Moment) => {
    const datePickerElement = document.getElementById('singleDateWrapper');
    let maxDateDifference = 1;
    let minDateDifference = 1;

    const localizedVisibleDate = getDateTime(false, ['local'], [[], []], visibleMonthDate);

    if (maxDateValue) {
      maxDateDifference = datesDifference(
        localizedVisibleDate,
        getDateTime(false, ['local'], [[], []], maxDateValue),
        'months',
      );
    }

    if (minDateValue) {
      minDateDifference = datesDifference(
        localizedVisibleDate,
        getDateTime(false, ['local'], [[], []], minDateValue),
        'months',
      );
    }

    maxDateDifference < 1
      ? datePickerElement?.classList.add('disabledRigthArrow')
      : datePickerElement?.classList.remove('disabledRigthArrow');

    minDateDifference < 1
      ? datePickerElement?.classList.add('disabledLeftArrow')
      : datePickerElement?.classList.remove('disabledLeftArrow');
  };

  const handleCalendarChange = (selectedCalendarDate: Moment | null) => {
    if (!selectedCalendarDate) {
      setTemporarySelectedDate(undefined);
      return;
    }

    const selectedMomentDate = moment(selectedCalendarDate);
    const boundedDate = clamp(selectedMomentDate);

    setTemporarySelectedDate(boundedDate);
    setErrorMessage('');
    onShownMonthChange(boundedDate.toDate());
  };

  const handleApply = () => {
    const boundedDate = temporarySelectedDate ? clamp(temporarySelectedDate) : undefined;
    setSelectedDate(boundedDate);

    const formattedDate = boundedDate ? boundedDate.format(SINGLE_DATE_PICKER_FORMAT) : '';
    setInputValue(formattedDate);
    onDateChange?.(toIso(boundedDate ?? null));
    closeCalendar();
  };

  const handleCancel = () => {
    setTemporarySelectedDate(selectedDate);
    closeCalendar();
  };

  const handleInputKeyDown = createDateInputKeyDownHandler(() => inputValue);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setInputValue(getFormattedDateInputValue(event.target.value));
    setErrorMessage('');
  };

  const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    setInputValue(getFormattedDateInputValue(event.clipboardData.getData('text') || ''));
    setErrorMessage('');
  };

  const parseToMomentStrict = (rawDateString: string) => {
    let parsedMomentDate = moment(rawDateString, SINGLE_DATE_PICKER_FORMAT, true);
    if (!parsedMomentDate.isValid()) {
      parsedMomentDate = moment(rawDateString, MARKET_DATA_DATE, true);
    }
    return parsedMomentDate;
  };

  const validateInput = () => {
    const trimmedInputValue = (inputValue || '').trim();

    if (!trimmedInputValue) {
      setSelectedDate(undefined);
      setTemporarySelectedDate(undefined);
      onDateChange?.(null);
      setErrorMessage('');
      return;
    }

    const parsedMomentDate = parseToMomentStrict(trimmedInputValue);
    const normalizedWireValue = parsedMomentDate.isValid()
      ? parsedMomentDate.format(MARKET_DATA_DATE)
      : trimmedInputValue;

    const isMaxDateValid = !!maxDateValue && !isBeforeDate(maxDateValue, normalizedWireValue);

    const isMinDateValid = !!minDateValue
      && isBeforeDate(
        getDateTime(true, ['minus'], [[1, 'day']], minDateValue),
        normalizedWireValue,
      );

    const isInvalid = !parsedMomentDate.isValid()
      || (maxDateMoment && !isMaxDateValid)
      || (minDateMoment && !isMinDateValid);

    if (isInvalid) {
      if (maxDateMoment && !isMaxDateValid) {
        setErrorMessage(
          lang.dateRangeEighteenYearsMessage(
            moment(maxDateValue).format(displayDateFormatNoDay),
          ),
        );
      } else if (minDateValue && !isMinDateValid) {
        setErrorMessage(
          lang.dateRangeEighteenYearsMessage(
            moment(minDateValue).format(displayDateFormatNoDay),
          ),
        );
      } else {
        setErrorMessage(lang.dateRangeEighteenYearsMessage(''));
      }

      return;
    }

    const boundedDate = clamp(parsedMomentDate);
    setSelectedDate(boundedDate);
    setTemporarySelectedDate(boundedDate);
    setInputValue(boundedDate.format(SINGLE_DATE_PICKER_FORMAT));
    onDateChange?.(toIso(boundedDate));
    setErrorMessage('');
  };

  const calendarValueMoment = temporarySelectedDate || selectedDate || maxDateMoment || moment();
  const calendarValueDate = calendarValueMoment.toDate();
  const handleIconKeyDown = createCalendarIconKeyDownHandler(openCalendarFromIcon);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <div className={styles.inputWrapper}>
        <form noValidate>
          <span ref={anchorRef} className={styles.anchor} aria-hidden="true" />

          <input
            ref={inputRef}
            id={idInput}
            className={classNames(
              styles.dateRanges,
              (isCalendarVisible || isInputFocused) && styles.activeInput,
            )}
            type="text"
            name="singleDateInput"
            inputMode="numeric"
            autoComplete="off"
            placeholder={SINGLE_DATE_PICKER_FORMAT}
            title={SINGLE_DATE_PICKER_FORMAT}
            maxLength={10}
            onKeyDown={handleInputKeyDown}
            onPaste={handlePaste}
            onChange={handleChange}
            onFocus={() => {
              setIsInputFocused(true);
              setErrorMessage('');
            }}
            onBlur={() => {
              setIsInputFocused(false);
              validateInput();
            }}
            value={inputValue}
            disabled={disabled}
            aria-label={label || 'date input'}
          />

          <span
            className={styles.iconWrapper}
            tabIndex={0}
            role="button"
            onClick={openCalendarFromIcon}
            onKeyDown={handleIconKeyDown}
            aria-label="Open calendar"
          >
            <img
              src={CalendarIcon}
              className={styles.icon}
              alt=""
              aria-hidden="true"
            />
          </span>
        </form>
      </div>

      {errorMessage && <p className={styles.errorMsg}>{errorMessage}</p>}

      <Popover
        id={idPopover}
        classes={{
          paper: classNames(
            styles.paper,
            pickerClasses.paperBase,
            {
              [styles.paperMobile]: isMobile,
              [pickerClasses.paperMobile]: isMobile,
            },
          ),
        }}
        open={isCalendarVisible}
        anchorEl={isMobile ? undefined : anchorElement}
        anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={
          isMobile
            ? { top: window.innerHeight / 2, left: window.innerWidth / 2 }
            : undefined
        }
        onClose={handleCancel}
        className={styles.popover}
        style={{
          backgroundColor: isMobile ? 'rgba(45, 45, 45, 0.9)' : undefined,
          zIndex: 4000,
          zoom:
            !isMobile && browserWidthSize <= ZOOM_BEHAVIOR_WIDTH
              ? `${Math.floor(browserWidthSize / 10).toString()}%`
              : '',
        }}
        anchorOrigin={{
          vertical: isMobile ? 'center' : 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: isMobile ? 'center' : 'top',
          horizontal: isMobile ? 'center' : 'right',
        }}
        TransitionProps={{ onEntered: () => onShownMonthChange(calendarValueDate) }}
      >
        <div
          id="singleDateWrapper"
          className={classNames(styles.dateRange, pickerClasses.pickerRoot)}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <StaticDatePicker
              value={calendarValueMoment}
              onChange={handleCalendarChange}
              onMonthChange={(newMonth: Moment | null) => {
                if (newMonth) onShownMonthChange(moment(newMonth).toDate());
              }}
              onYearChange={(newYear: Moment | null) => {
                if (newYear) onShownMonthChange(moment(newYear).toDate());
              }}
              minDate={minDateValue ? moment(minDateValue) : undefined}
              maxDate={maxDateValue ? moment(maxDateValue) : undefined}
              openTo="day"
              views={['year', 'month', 'day']}
              className={styles.dateRangePicker}
              dayOfWeekFormatter={(day) => day.format('ddd')}
            />
          </LocalizationProvider>

          <div className={styles.dateRangeButtons}>
            <Button
              variant={ButtonTypesEnum.Secondary}
              className={styles.cancelBtn}
              onClick={handleCancel}
            >
              {lang.commonCancelButton()}
            </Button>
            <Button
              variant={ButtonTypesEnum.Primary}
              className={styles.applyBtn}
              onClick={handleApply}
            >
              {lang.commonApply()}
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default SingleDatePickerWrapper;
